/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import * as React from 'react';
import './CurveGraph.scss';
import { Chart as ChartJS, LinearScale, CategoryScale, BarElement, PointElement, LineElement, Legend, Tooltip, Filler } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { BodyText } from '@vaisala-rockhopper/components';
import Annotation from 'chartjs-plugin-annotation';
import _ from 'lodash';
import { CurveData } from '../../../../modules/ChemicalDataController';
import { countDecimals } from '../utils/NumberUtils';

interface CurveGraphProps {
    curveInformation: CurveData
    theme: string
    calculatedResults: Array<{label: number, value: number}>
    calculatedPointResult: {label: number, value: number}
    nDAccuracy: string
    calculatedMinMaxPointResult: number[]
    chemicalMin: number,
    chemicalMax: number
}

ChartJS.register(Filler, LinearScale, CategoryScale, BarElement, PointElement, LineElement, Legend, Tooltip, Annotation);

export const CurveGraph = ({
    curveInformation,
    theme,
    calculatedResults,
    calculatedPointResult,
    nDAccuracy,
    calculatedMinMaxPointResult,
    chemicalMin,
    chemicalMax
}: CurveGraphProps) => {
  const chartRef = React.useRef();
  const [dataPointLabels, setDataPointLabels] = React.useState<number[]>([])
  const [datapoints, setDatapoints] = React.useState<number[]>([])

  const [singleDataPointLabel, setSingleDataPointLabel] = React.useState<number[]>([])
  const [singleDataPoint, setSingleDataPoint] = React.useState<number[]>([])
  const [annotation, ] = React.useState<any[]>([])

  const colors: any = {
    grid: {
      border: theme === 'vai-theme-light' ? 'rgba(105, 105, 105, 1.0)' : 'rgba(54, 54, 54, 1.0)',
      tick:  theme === 'vai-theme-light' ? 'rgba(228, 232, 237, 1.0)' : 'rgba(54, 54, 54, 1.0)',
    },
    h2o2Concentration: {
      border: theme === 'vai-theme-light' ? 'rgba(54, 54, 54, 1.0)' : 'rgba(228, 232, 237, 1.0)',
    },
    relativeSaturation: {
      border: theme === 'vai-theme-light' ? 'rgba(255, 150, 0, 0.8)' : 'rgba(255, 150, 0, 0.8)',
      bg: theme === 'vai-theme-light' ? 'rgba(255, 176, 26, 0.8)' : 'rgba(255, 176, 26, 0.8)',
    },
    relativeHumidity: {
      border: theme === 'vai-theme-light' ? 'rgba(0, 154, 200, 0.8)' : 'rgba(0, 103, 149, 0.8)',
      bg: theme === 'vai-theme-light' ? 'rgba(26, 180, 226, 0.8)' : 'rgba(0, 129, 175, 0.8)',
    },
    condensation: {
      border: theme === 'vai-theme-light' ? 'rgba(180, 10, 50, 1.0)' : 'rgba(180, 10, 50, 1.0)',
      bg: theme === 'vai-theme-light' ? 'rgba(180, 10, 50, 1.0)' : 'rgba(180, 10, 50, 1.0)',
    },
    activePhase: {
      bg: theme === 'vai-theme-light' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
    },
    tooltip: {
      font: theme === 'vai-theme-light' ? '#ffffff' : 'rgba(181, 181, 181, 1.0)',
      border: theme === 'vai-theme-light' ? '#003462' : 'rgba(255, 255, 255, 0.25)',
      bg: theme === 'vai-theme-light' ? '#003462' : '#101719',
    },
  };

  React.useEffect(() => {
    if (calculatedResults.length > 0) {
      let datasets: {labels: number[], datapoints: number[]} = {
        labels: [],
        datapoints: []
      }
      calculatedResults.forEach((elem: any, index: number) => {
        datasets.labels.push(elem.label)
        datasets.datapoints.push(elem.value)
      })
      setDataPointLabels(datasets.labels);
      setDatapoints(datasets.datapoints)
    }
  }, [calculatedResults])

  React.useEffect(() => {
    if (calculatedPointResult.value !== 0) {
      setSingleDataPointLabel([calculatedPointResult.label])
      setSingleDataPoint([calculatedPointResult.value])
    }
  }, [calculatedPointResult])

  const [minMaxValues, setMinMaxValues] = React.useState<Array<number>>([])
  React.useEffect(() => {
    if (calculatedMinMaxPointResult.length > 0) {
      setMinMaxValues(calculatedMinMaxPointResult)
    } else {
      setMinMaxValues([])
    }
  }, [calculatedMinMaxPointResult])

  React.useEffect(() => {
    const oldData = {...data}
    oldData.labels = dataPointLabels;
    oldData.datasets[0].data = datapoints;
    setData(oldData)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datapoints])

  React.useEffect(() => {
    const oldOptions = {...options}
    let min = dataPointLabels.findIndex((x: any) => x === singleDataPointLabel[0]);
    let labelContent = [''];

    oldOptions.plugins.annotation.annotations = [
      {
        id: "lineAnnotation",
        type: 'line',
        y1Min: chemicalMin,
        y1Max: chemicalMax,
        xMin: min,
        xMax: min,
        borderColor: 'rgba(255, 150, 0, 0.5)',
        borderWidth: 2,
        borderDash: [6,4],
        // display: isNaN(singleDataPointLabel[0]) ? false : true,
        display: (context: any, opts: any) => {
            if (!isNaN(singleDataPointLabel[0])) {
              return true
            }
            return false
            // return isNaN(singleDataPointLabel[0]) ? false : true
        }
      },
      {
        id: "arrow",
        type: 'polygon',
        // xValue: min > dataPointLabels.length / 2 ? min - 0.25 : min + 0.25,
        xValue: min,
        yValue: chemicalMax - (chemicalMax - chemicalMin) / 2,
        sides: 3,
        radius: 8,
        rotation: min > dataPointLabels.length / 2 ? 90 : 270,
        backgroundColor: colors.tooltip.bg,
        xAdjust: min > dataPointLabels.length / 2 ? -10 : 10,
        display: (context: any, opts: any) => {
          return min !== -1;
        },
      },
      {
        id: "labelAnnotation",
        type: 'label',
        xValue: min > dataPointLabels.length / 2 ? min - dataPointLabels.length / 7 : min + dataPointLabels.length / 7,
        xAdjust: min > dataPointLabels.length / 2 ? 0 : 0,
        yValue: chemicalMax - (chemicalMax - chemicalMin) / 2,
        backgroundColor: colors.tooltip.bg,
        color: colors.tooltip.font,
        content: labelContent,
        font: {
          size: 14
        },
        display: (context: any, opts: any) => {
          return min !== -1;
        },
      }
    ].flat()
    setOptions(oldOptions)
    // chartRef.current.chart.update();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleDataPoint, minMaxValues])

  const afterDrawPlugin = {
    id: 'afterDraw',
    afterDraw: (chart: any) => {
      if (chart.tooltip?._active?.length) {
        let x = chart.tooltip._active[0].element.x;
        let yAxis = chart.scales.y;
        let ctx = chart.ctx;
        ctx.save();
        ctx.beginPath();
        ctx.setLineDash([6, 4]);
        ctx.moveTo(x, yAxis.top);
        ctx.lineTo(x, yAxis.bottom);
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgb(255, 150, 0)';
        ctx.stroke();
        ctx.restore();
      }
    },
  }

  // Graph data and options
  const [data, setData] = React.useState({
    labels: dataPointLabels,
    datasets: [
      {
        label: "",
        data: [0],
        fill: false,
        tension: 0.4,
        borderColor: '#009ac8',
        pointRadius: 1,
        pointHoverRadius: 5,
        showLine: true,
      }
    ]
  });

  const [options, setOptions] = React.useState({
    interaction: {
      intersect: false,
      mode: 'index' as 'index'
    },
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      annotation: {
        animation: {
          duration: 0
        },
        annotations: [annotation].flat(),
        enter: (context: any) => {
            // const thisannotation = ((context.chart.options.plugins!.annotation!.annotations as any)[id] as AnnotationOptions);
            // thisannotation.display = true
            context.element.options.display = true;
            context.chart.update();
        },
        leave: (context: any) => {
            context.element.options.display = true;
            context.chart.update();
        },
      },
      tooltip: {
        backgroundColor: colors.tooltip.bg,
        titleColor: colors.tooltip.font,
        titleFont: {
          size: 14,
        },
        bodyFont: {
          size: 14,
        },
        bodyColor: colors.tooltip.font,
        titleMarginBottom: 8,
        bodySpacing: 8,
        caretPadding: 16,
        padding: {
          top: 8,
          right: 8,
          bottom: 8,
          left: 8,
        },
        cornerRadius: 0,
        caretSize: 8,
        borderColor: colors.tooltip.border,
        borderWidth: 1,
        boxWidth: 0,
        boxHeight: 0,
        callbacks: {
          title: () => {
            return ""
          },
          label: (item: any) => {
             return [
              `${item.label} nD`,
              `${item.formattedValue} ${curveInformation.Unit}`
            ]
          }
        }
      }
    },
    scales: {
      y: {
        suggestedMin: _.round(chemicalMin, countDecimals(chemicalMin)),
        suggestedMax: _.round(chemicalMax, countDecimals(chemicalMax)),
        min: _.round(chemicalMin, countDecimals(chemicalMin)),
        max: _.round(chemicalMax, countDecimals(chemicalMax)),
        display: true,
        position: 'left' as const,
        grid: {
          borderColor: colors.grid.border,
          tickColor: colors.grid.tick
        },
        ticks: {
          crossAlign: "far" as const,
          stepSize: 20,
          color: colors.grid.tick
        },
      },
      x: {
        display: true,
        beginAtZero: false,
        grid: {
          borderColor: colors.grid.border,
          tickColor: colors.grid.tick
        },
        ticks: {
          color: colors.grid.tick,
          maxTicksLimit: 10,
          maxRotation: 0,
          minRotation: 0
        }
      },
    }
  })

  React.useEffect(() => {
    const oldOptions = {...options};
    if (!isNaN(singleDataPointLabel[0])) {
      let labelContent = []
      if (minMaxValues.length > 0) {
        labelContent = [
          _.round(singleDataPointLabel[0], 3) + ' \xB1 ' + nDAccuracy + ' nD',
          _.round(singleDataPoint[0], 3) + ' ' + curveInformation.Unit,
          '',
          _.round(singleDataPointLabel[0] - parseFloat(nDAccuracy), 3) + ': ' +  _.round(minMaxValues[0], 3),
          _.round(singleDataPointLabel[0] + parseFloat(nDAccuracy), 3) + ': ' + _.round(minMaxValues[1], 3),
        ]
      } else {
        labelContent = [_.round(singleDataPointLabel[0], 3) + ' nD', _.round(singleDataPoint[0], 3) + ' ' + curveInformation.Unit]
      }
      const changeIndex = oldOptions.plugins.annotation.annotations.findIndex(annotation => annotation.id === "labelAnnotation");
      oldOptions.plugins.annotation.annotations[changeIndex].content = labelContent
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleDataPointLabel, minMaxValues])

  const yAxisTitle = (
    <div className="graph__y-axis-title-container">
      <div className="left">
        <BodyText>{curveInformation.Unit}</BodyText>
      </div>
    </div>
  );
  const xAxisTitle = (
    <div className="graph__x-axis-title-container">
      <div className="right">
          <div className="bottom">
            <BodyText>nD</BodyText>
          </div>
      </div>
    </div>
  )

  // const ctx = document.getElementById('canvas') as HTMLCanvasElement
  // let calibrationChart = new ChartJS(ctx, {
  //   type: "line",
  //   data: data,
  //   options: options,
  //   plugins: [afterDrawPlugin]
  // })

  return (
    <div className={`graph graph--desktop graph--active'} ${theme}`}>
      <div>
        <div className="graph__grid">
          <div className="graph__grid-item">
                {yAxisTitle}
                {datapoints.length > 0 && (
                  <div id="canvas">

                    <Line ref={chartRef} data={data} options={options} plugins={[afterDrawPlugin]} redraw />
                  </div>
                )}
                {xAxisTitle}
                {/* {renderCustomLabels()} */}
            <div className="gtm-content gtm-content--desktop-calculator" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurveGraph;
